import { type ReactNode } from 'react';
import { Chip, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { BatchWorkflowListItem, BatchPriority, SlaUrgency } from '../../types/workflow';
import { WorkflowStatusChip } from './WorkflowStatusChip';
import { drisWorkflowColors } from '../../theme/colors';
import { SampleTypeIndicator } from '../shared/SampleTypeIndicator';
import { AirApprovalIndicator } from '../shared/AirApprovalIndicator';

/**
 * Registry of the workflow list's configurable columns — the single source of truth for the
 * table header AND each row cell. The ID column is deliberately NOT here: it stays a fixed
 * left-hand anchor (always visible, never reorderable) so the list can never render empty and
 * always has a stable identity column.
 *
 * Visibility and left-to-right order are user-configurable (see usePersistentColumnConfig +
 * ColumnConfigDialog). To add a column: add a WorkflowColumnDef below AND add every batch field
 * its renderCell reads to RENDER_AFFECTING_BATCH_FIELDS, or BatchWorkflowRow's memo will show
 * stale data after a refetch.
 */
export type WorkflowColumnKey =
  | 'batch'
  | 'client'
  | 'farm'
  | 'farmer'
  | 'cropVariety'
  | 'internalRef'
  | 'status'
  | 'air'
  | 'priority'
  | 'date'
  | 'urgency'
  | 'type'
  | 'samples'
  | 'responsible';

export interface WorkflowColumnDef {
  key: WorkflowColumnKey;
  /** Header text and the label shown in the column-config dialog. */
  label: string;
  /**
   * When set, the header renders a sortable TableSortLabel bound to this key. Only set it for
   * keys the backend actually sorts (WorkflowQueryService.ApplySorting): batchNumber, farmName,
   * farmerName, statusCode, priority, createdAt. Client is intentionally NOT sortable — its name
   * is resolved in-memory after the DB query, so a sort control would be misleading.
   */
  sortKey?: string;
  /** Horizontal alignment for both the header cell and the body cell. */
  align?: 'center';
  /** Visibility when the user has no saved preference. */
  defaultVisible: boolean;
  renderCell: (batch: BatchWorkflowListItem) => ReactNode;
}

// --- cell helpers (moved verbatim from BatchWorkflowRow) -----------------------------------

const getPriorityChipProps = (
  p: BatchPriority,
): { sx: Record<string, unknown>; variant?: 'outlined' | 'filled' } => {
  const colors = drisWorkflowColors.priority;
  const base = { minWidth: 80 };
  switch (p) {
    case 'Urgent':
      return { sx: { ...base, bgcolor: colors.Urgent.bg, color: colors.Urgent.text, fontWeight: 600 } };
    case 'High':
      return { sx: { ...base, bgcolor: colors.High.bg, color: colors.High.text } };
    case 'Low':
      return { variant: 'outlined', sx: { ...base, borderColor: 'grey.400', color: 'text.secondary' } };
    default:
      return { variant: 'outlined', sx: { ...base, borderColor: 'grey.300', color: 'text.secondary' } };
  }
};

const getSlaChipColor = (urgency: SlaUrgency): 'error' | 'warning' | 'success' | 'default' => {
  switch (urgency) {
    case 'overdue':
      return 'error';
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'ok':
      return 'success';
    case 'completed':
      return 'default';
    default:
      return 'default';
  }
};

const getSlaChipVariant = (urgency: SlaUrgency): 'filled' | 'outlined' => {
  switch (urgency) {
    case 'overdue':
      return 'filled';
    case 'warning':
      return 'filled';
    default:
      return 'outlined';
  }
};

const getSlaChipLabel = (urgency: SlaUrgency, daysRemaining: number): string => {
  if (urgency === 'completed') return 'Completed';
  if (urgency === 'overdue') return `${Math.abs(daysRemaining)}d overdue`;
  return `${daysRemaining}d left`;
};

// --- the registry --------------------------------------------------------------------------

export const WORKFLOW_COLUMNS: WorkflowColumnDef[] = [
  {
    key: 'batch',
    label: 'Batch',
    sortKey: 'batchNumber',
    defaultVisible: false,
    renderCell: (b) => (
      <>
        <Typography variant="body2" fontWeight={500}>
          {b.batchNumber}
        </Typography>
        {b.referenceNumber && b.referenceNumber !== b.batchNumber && (
          <Typography variant="caption" color="text.secondary">
            {b.referenceNumber}
          </Typography>
        )}
      </>
    ),
  },
  {
    key: 'client',
    label: 'Client',
    defaultVisible: true,
    renderCell: (b) => (
      <Typography variant="body2" fontWeight={500}>
        {b.clientName || '-'}
      </Typography>
    ),
  },
  {
    key: 'farm',
    label: 'Farm',
    sortKey: 'farmName',
    defaultVisible: true,
    renderCell: (b) => <Typography variant="body2">{b.farmName || '-'}</Typography>,
  },
  {
    key: 'farmer',
    label: 'Farmer',
    sortKey: 'farmerName',
    defaultVisible: true,
    renderCell: (b) => <Typography variant="body2">{b.farmerName || '-'}</Typography>,
  },
  {
    key: 'cropVariety',
    label: 'Crop & Variety',
    defaultVisible: true,
    renderCell: (b) =>
      b.cropVarieties?.length ? (
        b.cropVarieties.map((cv, i) => (
          <Typography key={i} variant="body2" sx={{ lineHeight: 1.4, fontSize: '0.8rem' }}>
            {cv}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" color="text.disabled">
          -
        </Typography>
      ),
  },
  {
    key: 'internalRef',
    label: 'Internal Ref',
    defaultVisible: true,
    renderCell: (b) => <Typography variant="body2">{b.clientRef || '-'}</Typography>,
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'statusCode',
    defaultVisible: true,
    renderCell: (b) =>
      b.statusCode ? (
        <WorkflowStatusChip statusCode={b.statusCode} statusName={b.statusName} size="small" />
      ) : (
        <Typography variant="body2" color="text.disabled">
          -
        </Typography>
      ),
  },
  {
    key: 'air',
    label: 'Approved',
    defaultVisible: true,
    renderCell: (b) => (
      <AirApprovalIndicator
        reportPdfApproved={b.reportPdfApproved}
        interpretationApproved={b.interpretationApproved}
        recommendationApproved={b.recommendationApproved}
        size="small"
      />
    ),
  },
  {
    key: 'priority',
    label: 'Priority',
    sortKey: 'priority',
    defaultVisible: true,
    renderCell: (b) => <Chip label={b.priority} size="small" {...getPriorityChipProps(b.priority)} />,
  },
  {
    key: 'date',
    label: 'Date',
    sortKey: 'createdAt',
    defaultVisible: true,
    renderCell: (b) => (
      <Typography variant="body2" color="text.secondary">
        {format(new Date(b.createdAt), 'dd/MM/yy')}
      </Typography>
    ),
  },
  {
    key: 'urgency',
    label: 'Urgency',
    defaultVisible: true,
    renderCell: (b) => (
      <Chip
        label={getSlaChipLabel(b.slaUrgency, b.daysRemaining)}
        size="small"
        color={getSlaChipColor(b.slaUrgency)}
        variant={getSlaChipVariant(b.slaUrgency)}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    ),
  },
  {
    key: 'type',
    label: 'Type',
    align: 'center',
    defaultVisible: true,
    renderCell: (b) => (
      <SampleTypeIndicator
        hasSap={(b.expectedSapCount ?? 0) > 0}
        hasTissue={(b.expectedTissueCount ?? 0) > 0}
        hasSoil={(b.expectedSoilCount ?? 0) > 0}
        size="small"
      />
    ),
  },
  {
    key: 'samples',
    label: 'Samples',
    align: 'center',
    defaultVisible: true,
    renderCell: (b) => (
      <>
        {b.totalActualSamples}/{b.totalExpectedSamples}
      </>
    ),
  },
  {
    key: 'responsible',
    label: 'Responsible',
    defaultVisible: false,
    renderCell: (b) => (
      <Typography variant="body2" color="text.secondary">
        {b.responsibleRoles?.length ? b.responsibleRoles.join(', ') : '-'}
      </Typography>
    ),
  },
];

export const DEFAULT_COLUMN_ORDER: WorkflowColumnKey[] = WORKFLOW_COLUMNS.map((c) => c.key);

export const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = Object.fromEntries(
  WORKFLOW_COLUMNS.map((c) => [c.key, c.defaultVisible]),
);

export const WORKFLOW_COLUMN_BY_KEY: Record<string, WorkflowColumnDef> = Object.fromEntries(
  WORKFLOW_COLUMNS.map((c) => [c.key, c]),
);

/**
 * Every batch field that affects a rendered row — read by any column's renderCell above OR by the
 * row's fixed cells (ID, and the Actions cell, which reads batchId/soilBatchId). BatchWorkflowRow's
 * React.memo comparator iterates this list; a field omitted here means that cell will not update
 * after a refetch that changes only that field. Keep it in sync when adding/altering columns.
 */
export const RENDER_AFFECTING_BATCH_FIELDS: (keyof BatchWorkflowListItem)[] = [
  'id',
  'batchId',
  'soilBatchId',
  'batchNumber',
  'referenceNumber',
  'clientName',
  'farmName',
  'farmerName',
  'clientRef',
  'cropVarieties',
  'statusCode',
  'statusName',
  'reportPdfApproved',
  'interpretationApproved',
  'recommendationApproved',
  'priority',
  'createdAt',
  'slaUrgency',
  'daysRemaining',
  'expectedSapCount',
  'expectedTissueCount',
  'expectedSoilCount',
  'totalActualSamples',
  'totalExpectedSamples',
  'responsibleRoles',
  'isOverdue',
];
